import { convertCentsToYuan } from "@/utils/math"

export type DiscountStatisticsMode = "allocation" | "type"

export interface DiscountTypeStatisticLike {
  discountType?: number | null
  discountName?: string | null
  orderCount?: number | null
  discountAmount?: number | null
}

export interface DiscountTypeStatisticsViewRow {
  key: string
  name: string
  count: number
  storedAmount: number
  displayAmount: number
}

export interface DiscountTypeStatisticsView {
  rows: DiscountTypeStatisticsViewRow[]
  storedTotal: number
  displayTotal: number
}

export interface DiscountStatisticsComparisonInput {
  discountAmount?: number | null
  otherDiscountAmount?: number | null
  settlementAdjustmentAmount?: number | null
  statistics?: DiscountTypeStatisticLike[] | null
}

export interface DiscountStatisticsComparison {
  allocationDiscountAmount: number
  otherDiscountAmount: number
  settlementAdjustmentAmount: number
  allocationNetAmount: number
  typeNetAmount: number
  differenceAmount: number
  isAligned: boolean
  adjustmentOperator: "+" | "-"
}

const numeric = (value?: number | null) => {
  const amount = Number(value ?? 0)
  return Number.isFinite(amount) ? amount : 0
}

export function buildDiscountTypeStatisticsView(
  statistics?: DiscountTypeStatisticLike[] | null,
): DiscountTypeStatisticsView {
  const rows = (statistics ?? [])
    .map((item) => {
      const storedAmount = numeric(item.discountAmount)
      const count = Math.max(0, Math.trunc(numeric(item.orderCount)))
      const name = item.discountName?.trim() || "其他优惠"
      const direction = storedAmount < 0 ? "reverse" : "reduction"
      return {
        key: `${item.discountType ?? "unknown"}:${name}:${direction}`,
        name,
        count,
        storedAmount,
        displayAmount: -storedAmount,
      }
    })
    .filter(item => item.count > 0 && item.storedAmount !== 0)
  const storedTotal = rows.reduce((total, item) => total + item.storedAmount, 0)
  return {
    rows,
    storedTotal,
    displayTotal: -storedTotal,
  }
}

export function discountTypeStoredTotal(statistics?: DiscountTypeStatisticLike[] | null) {
  return buildDiscountTypeStatisticsView(statistics).storedTotal
}

export function buildDiscountStatisticsComparison({
  discountAmount,
  otherDiscountAmount,
  settlementAdjustmentAmount,
  statistics,
}: DiscountStatisticsComparisonInput): DiscountStatisticsComparison {
  const allocationDiscountAmount = numeric(discountAmount)
  const normalizedOtherDiscountAmount = numeric(otherDiscountAmount)
  const normalizedSettlementAdjustmentAmount = numeric(settlementAdjustmentAmount)
  const allocationNetAmount = allocationDiscountAmount
    + normalizedOtherDiscountAmount
    - normalizedSettlementAdjustmentAmount
  const typeNetAmount = discountTypeStoredTotal(statistics)
  const differenceAmount = allocationNetAmount - typeNetAmount

  return {
    allocationDiscountAmount,
    otherDiscountAmount: normalizedOtherDiscountAmount,
    settlementAdjustmentAmount: normalizedSettlementAdjustmentAmount,
    allocationNetAmount,
    typeNetAmount,
    differenceAmount,
    isAligned: differenceAmount === 0,
    adjustmentOperator: normalizedSettlementAdjustmentAmount < 0 ? "+" : "-",
  }
}

export function calculateTypeStatisticsResidual({
  turnover,
  actualIncome,
  statistics,
}: {
  turnover: number
  actualIncome: number
  statistics?: DiscountTypeStatisticLike[] | null
}) {
  return numeric(actualIncome) + discountTypeStoredTotal(statistics) - numeric(turnover)
}

export function formatDiscountTypeDisplayAmount(storedAmount?: number | null): string {
  const amount = numeric(storedAmount)
  const formatted = convertCentsToYuan(Math.abs(amount), false)
  if (amount > 0) return `-${formatted}`
  if (amount < 0) return `+${formatted}`
  return formatted
}
